// @ts-check

/** @file Live-query adapters for the non-tenant catalog database. */

/** @import RecordModel from "velocious/build/src/database/record/index.js" */
/** @import ModelClassQuery from "velocious/build/src/database/query/model-class-query.js" */
/** @typedef {typeof RecordModel} RecordModelClass */

/**
 * Builds a live count source for a catalog model query.
 * @template {RecordModelClass} MC - Catalog model class.
 * @param {{modelClass: MC, query?: (query: ModelClassQuery<MC>) => ModelClassQuery<MC>}} args Source arguments.
 * @returns {{getModelClass: () => MC, toArray: () => Promise<number[]>}} Live count source.
 */
export function localCountSource({modelClass, query}) {
  return Object.freeze({
    /**
     * Returns the model observed by React live-query subscriptions.
     * @returns {MC} Catalog model class.
     */
    getModelClass() {
      return modelClass
    },

    /**
     * Executes the configured count against the default catalog database.
     * @returns {Promise<number[]>} Resolves with the live count result.
     * @rejects {Error} When the query model is mismatched or count execution fails.
     */
    async toArray() {
      const builtQuery = query ? query(modelClass.all()) : modelClass.all()

      if (builtQuery.getModelClass().getModelName() !== modelClass.getModelName()) {
        throw new Error("Local count builder returned a query for another model")
      }

      return [await builtQuery.count()]
    }
  })
}
